/**
 * Plugin Configuration Hook
 * Uses PluginConfigUtils for business logic
 */

import { useMemo } from 'react';

import { useTagadaContext } from '../providers/TagadaProvider';
import { PluginConfigUtils, PluginConfig, RawPluginConfig } from '../../core/utils/pluginConfig';

export interface UsePluginConfigOptions {
    rawConfig?: RawPluginConfig;
}

export interface UsePluginConfigResult<TConfig = Record<string, any>> {
    config: PluginConfig<TConfig>;
    storeId?: string;
    accountId?: string;
    basePath?: string;
    loading: boolean;
    error?: Error;
    isValid: boolean;
}

export function usePluginConfig<TConfig = Record<string, any>>(
    options: UsePluginConfigOptions = {}
): UsePluginConfigResult<TConfig> {
    const { pluginConfig, pluginConfigLoading } = useTagadaContext();

    const config = useMemo(() => {
        const baseConfig = PluginConfigUtils.getPluginConfig(
            options.rawConfig,
            {
                storeId: pluginConfig.storeId,
                accountId: pluginConfig.accountId,
                basePath: pluginConfig.basePath,
            }
        );

        // Merge config properties from the loaded plugin config
        return {
            ...baseConfig,
            ...pluginConfig.config,
            storeId: pluginConfig.storeId,
            accountId: pluginConfig.accountId,
            basePath: pluginConfig.basePath,
        } as PluginConfig<TConfig>;
    }, [options.rawConfig, pluginConfig]);

    const isValid = useMemo(() => {
        return PluginConfigUtils.validateConfig(config as PluginConfig<Record<string, any>>);
    }, [config]);

    return {
        config,
        storeId: config.storeId,
        accountId: config.accountId,
        basePath: config.basePath,
        loading: pluginConfigLoading,
        error: undefined, // TODO: Add error handling if needed
        isValid,
    };
}
